import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { callPostApi } from "../../api/ApiCaller";
import { SET_GAME_BONUS_RESULT_POST } from "../../api/ApiConst";
import { TV_SND, playTv, chainTv, stopTv } from "./tvSound";

// TV Millionario's LUCKY WHEEL bonus, as played in
// https://www.youtube.com/watch?v=3VRlTIPI0To (1:52-3:24). Drawn over the
// reels on the 800x600 stage; the game's footer stays in view and shows
// this bonus's messages and GANA. Positions come from registering the video
// onto the stage (crop 37,49 816x380 at 854x480) and template-matching
// public/tv-milionario/bonus/whell-bonus; timings were counted off its frames.
//
// The pot (BONUS in the middle) is the server's bonus amount. The wheel
// always stops on BONUS and pays it; the xN circles are scenery. GANA counts
// the pot in on top of the spin's line win, then the game pays GANA into
// CRÉDITO behind YOU WIN, as at 3:26.

const WH = `${process.env.PUBLIC_URL}/tv-milionario/bonus/whell-bonus`;
const REF = `${WH}/refactored`;
const pad2 = (n) => String(n).padStart(2, "0");

// the ring: 16 circles 22.5° apart, radius 151 round (400,293), clockwise
// from the top as at 2:00 (B = BONUS)
const RING = ["B", 11, 4, 13, 8, 9, 5, 11, 10, "B", 7, 6, 12, 2, 14, 3];
const CX = 400;
const CY = 293;
const R = 151;
const STEP_DEG = 360 / RING.length;
// circles_00 BONUS, 01..13 x2..x14; 15..27 the same, lit (under the arrow)
const circleSrc = (v, lit) => (v === "B"
    ? `${REF}/bonus_hightlight/bonus_hightlight_${lit ? 1 : 0}.png`
    : `${REF}/circles/circles_${pad2(v - 1 + (lit ? 14 : 0))}.png`);

// timings (ms)
const OPEN_FRAME_MS = 70;     // abertura: LUCKY WHEEL title, 19 frames, over black
const TITLE_MS = 1500;
const RISE_MS = 800;          // the wheel rises from below the screen
const SPIN_AFTER_MS = 700;    // ...and starts turning this long after
const SPIN_STEP_MS = 66;      // it turns a whole circle at a time, ~15 a second
const STOP_MIN_STEPS = 10;    // after INICIAR it runs on at least this far, to a BONUS
const COUNTDOWN = 5;          // PRESIONE INICIAR 5..0, then it stops by itself
const BIG_AFTER_MS = 5300;    // stopped -> the big BONUS <pot> comes up
const COUNT_AFTER_MS = 2800;  // ...-> the pot starts counting into GANA
const COUNT_RATE = 750;       // credits a second, as in the video...
const COUNT_MAX_MS = 15000;   // ...but never longer than this (60000 took 80s there)
const EMPTY_MS = 1200;        // BONUS 0 in the middle, then the wheel drops away
const DROP_MS = 500;
const MSG_SWAP_MS = 1000;     // USTED GANO EL BONO <-> FELICIDADES

// green_numeral: 10 glyphs 76x87; yellow_numeral: 10 glyphs 15x22
const Digits = ({ value, src, w, h, gap = 0 }) => (
    <span style={{ display: "inline-flex", gap }}>
        {String(Math.max(0, Math.floor(value))).split("").map((d, i) => (
            <span key={i} style={{
                width: w, height: h, backgroundImage: `url(${src})`,
                backgroundSize: `${w * 10}px ${h}px`, backgroundPosition: `${-Number(d) * w}px 0`,
            }} />
        ))}
    </span>
);

export const preloadLuckyWheel = () => {
    const srcs = [`${WH}/panel.png`, `${WH}/bonus.png`, `${WH}/green_numeral.png`, `${WH}/yellow_numeral.png`];
    for (let f = 0; f < 19; f++) srcs.push(`${REF}/abertura/abertura_${pad2(f)}.png`);
    for (let f = 0; f < 28; f++) srcs.push(`${REF}/circles/circles_${pad2(f)}.png`);
    for (let f = 0; f < 34; f++) srcs.push(`${REF}/fountain_money/fountain_money_${pad2(f)}.png`);
    for (let f = 0; f < 18; f++) srcs.push(`${REF}/fountain_logo/fountain_logo_${pad2(f)}.png`);
    srcs.forEach((s) => { const im = new Image(); im.src = s; });
};

// lineWin: the spin's own win, already in GANA. onGana(value) and
// onMessage(key) drive the footer; onDone(total) hands back line win + pot.
// testPot: a made-up pot, no server call (dev).
const LuckyWheelBonus = ({ gameResult, gameSessionRecordId, keyboardInput, lineWin = 0, testPot, Button, onGana, onMessage, onDone }) => {
    const [t0] = useState(Date.now);
    const [now, setNow] = useState(Date.now());
    const [pot, setPot] = useState(null);           // from the server
    const [steps, setSteps] = useState(0);          // how far the ring has turned
    const [stopping, setStopping] = useState(null); // the step count it will stop on
    const [stoppedAt, setStoppedAt] = useState(null);
    const [countdown, setCountdown] = useState(COUNTDOWN);
    const [left, setLeft] = useState(null);         // pot still to count into GANA
    const [endedAt, setEndedAt] = useState(null);
    const total = useRef(0);
    const alive = useRef(true);
    const state = useRef({});
    state.current = { steps, stopping, stoppedAt, left, pot };

    // the machine's music (3VRlTIPI0To 111.0s, ECdzTa0tdpA 86.6s): the title
    // and rise run into the turning loop; at the stop the fountain piece
    // (8.2s, = BIG_AFTER_MS + COUNT_AFTER_MS), then the count loop till GANA is full
    useEffect(() => {
        chainTv(TV_SND.wheelIntro, TV_SND.wheelSpinLoop);
        return () => stopTv(TV_SND.wheelIntro, TV_SND.wheelSpinLoop, TV_SND.wheelStop, TV_SND.wheelCountLoop);
    }, []);

    useEffect(() => {
        alive.current = true;
        const id = setInterval(() => setNow(Date.now()), 33);
        const got = (p, tot) => {
            if (!alive.current) return;
            setPot(p);
            total.current = tot;
        };
        if (testPot !== undefined) {
            setTimeout(() => got(testPot, lineWin + testPot), 50);
        } else {
            callPostApi(SET_GAME_BONUS_RESULT_POST, { bonusType: gameResult?.bonusType, recordId: gameSessionRecordId }, {}, (response) => {
                if (response?.errorCode !== 0) {
                    toast.error(response?.message || "Bonus result not loaded");
                    got(0, lineWin);
                    return;
                }
                const res = response.responsePacket || {};
                const tot = Number(res.totalWinnings ?? 0) || 0;
                const bonusPart = Number(res.totalBonusWinnings ?? (tot - lineWin)) || 0;
                got(Math.max(0, bonusPart), Math.max(tot, lineWin + Math.max(0, bonusPart)));
            }, (error) => {
                toast.error(typeof error === "string" ? error : "Bonus result not loaded");
                got(0, lineWin);
            });
        }
        return () => { alive.current = false; clearInterval(id); };
    }, []);

    const t = now - t0;
    const risen = TITLE_MS + RISE_MS;
    const spinning = t >= risen + SPIN_AFTER_MS && stoppedAt === null && pot !== null;

    // the ring turns a circle at a time; once told to stop it runs on to its mark
    useEffect(() => {
        if (!spinning) return;
        const id = setInterval(() => {
            const { steps: s, stopping: target } = state.current;
            if (target !== null && s + 1 >= target) {
                setSteps(target);
                setStoppedAt(Date.now());
                clearInterval(id);
                stopTv(TV_SND.wheelIntro, TV_SND.wheelSpinLoop);
                playTv(TV_SND.wheelStop);
                return;
            }
            setSteps(s + 1);
        }, SPIN_STEP_MS);
        return () => clearInterval(id);
    }, [spinning]);

    const stop = () => {
        const { steps: s, stopping: target, stoppedAt: done } = state.current;
        if (target !== null || done !== null || !spinning) return;
        // the circle under the arrow after n steps is RING[(-n) mod 16]
        let n = s + STOP_MIN_STEPS;
        while (RING[((-n % 16) + 16) % 16] !== "B") n++;
        playTv(TV_SND.selPick);
        setStopping(n);
    };

    // PRESIONE INICIAR 5..0 while it turns
    useEffect(() => {
        if (!spinning || stopping !== null) return;
        onMessage("press_init");
        const id = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(id);
    }, [spinning, stopping]);
    useEffect(() => {
        if (countdown > 0 && countdown < COUNTDOWN) playTv(TV_SND.selTick);
        if (countdown === 0) stop();
    }, [countdown]);

    // won: messages swap, the big number, then the count into GANA
    const sinceStop = stoppedAt === null ? -1 : now - stoppedAt;
    const won = stoppedAt !== null;
    useEffect(() => {
        if (!won) return;
        let k = 0;
        onMessage("bonus");
        const id = setInterval(() => onMessage(++k % 2 ? "congrats" : "bonus"), MSG_SWAP_MS);
        return () => clearInterval(id);
    }, [won]);
    useEffect(() => {
        if (!won) return;
        const big = setTimeout(() => setLeft(state.current.pot), BIG_AFTER_MS);
        return () => clearTimeout(big);
    }, [won]);

    const counting = left !== null && endedAt === null && sinceStop >= BIG_AFTER_MS + COUNT_AFTER_MS;
    useEffect(() => {
        if (!counting) return;
        stopTv(TV_SND.wheelStop);
        playTv(TV_SND.wheelCountLoop, true);
        const p = state.current.pot;
        const rate = Math.max(COUNT_RATE, (p * 1000) / COUNT_MAX_MS);
        const start = Date.now();
        const id = setInterval(() => {
            const paid = Math.min(p, Math.floor(((Date.now() - start) / 1000) * rate));
            setLeft(p - paid);
            onGana(lineWin + paid);
            if (paid >= p) { clearInterval(id); setEndedAt(Date.now()); }
        }, 33);
        return () => clearInterval(id);
    }, [counting]);

    // INICIAR during the count lands the rest at once
    const finishCount = () => {
        if (state.current.left === null || endedAt !== null) return;
        setLeft(0);
        onGana(lineWin + state.current.pot);
        setEndedAt(Date.now());
    };

    useEffect(() => {
        if (endedAt === null) return;
        stopTv(TV_SND.wheelStop, TV_SND.wheelCountLoop);
        const id = setTimeout(() => onDone(total.current), EMPTY_MS + DROP_MS);
        return () => clearTimeout(id);
    }, [endedAt]);

    const press = () => (state.current.stoppedAt === null ? stop() : finishCount());
    useEffect(() => {
        if (!keyboardInput) return;
        const onKey = (e) => { if (e.key.toLowerCase() === "b") press(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [keyboardInput, spinning, endedAt]);

    // ---- drawing ----
    const abs = (style) => ({ position: "absolute", display: "block", pointerEvents: "none", ...style });
    const sinceEnd = endedAt === null ? -1 : now - endedAt;
    // the wheel's top edge: rising, in place, or dropping away
    let wheelY = 2;
    if (t < TITLE_MS) wheelY = 600;
    else if (t < risen) { const k = (t - TITLE_MS) / RISE_MS; wheelY = 2 + 598 * (1 - k) * (1 - k); }
    if (sinceEnd > EMPTY_MS) wheelY = 2 + 598 * Math.min(1, (sinceEnd - EMPTY_MS) / DROP_MS);
    const celebrating = won && endedAt === null;
    const showBig = left !== null && endedAt === null;

    return (
        <div className="tvm-wheel">
            {t < TITLE_MS && (
                <img alt="" src={`${REF}/abertura/abertura_${pad2(Math.min(18, Math.floor(t / OPEN_FRAME_MS)))}.png`}
                    style={abs({ left: 77, top: 143 })} />
            )}
            <div style={abs({ left: 0, top: wheelY - 2, width: 800, height: 492 })}>
                <img alt="" src={`${WH}/panel.png`} style={abs({ left: 0, top: 2 })} />
                <img alt="" src={`${REF}/logo/logo_${pad2(Math.floor(now / 120) % 12)}.png`} style={abs({ left: 252, top: 24 })} />
                {celebrating && (
                    <>
                        <img alt="" src={`${REF}/fountain_logo/fountain_logo_${pad2(Math.floor(sinceStop / 60) % 9)}.png`} style={abs({ left: 176, top: 112 })} />
                        <img alt="" src={`${REF}/fountain_logo/fountain_logo_${pad2(9 + (Math.floor(sinceStop / 60) % 9))}.png`} style={abs({ left: 539, top: 112 })} />
                    </>
                )}
                {RING.map((v, i) => {
                    const a = ((i + steps) * STEP_DEG * Math.PI) / 180;
                    const lit = ((i + steps) % 16 + 16) % 16 === 0;
                    return <img key={i} alt="" src={circleSrc(v, lit)}
                        style={abs({ left: CX + R * Math.sin(a) - 32, top: CY - R * Math.cos(a) - 32, width: 64, height: 64 })} />;
                })}
                <img alt="" src={`${REF}/arrow/arrow_${pad2(spinning ? Math.floor(now / 66) % 13 : 0)}.png`} style={abs({ left: 386, top: 79 })} />
                {/* the pot, small, in the medallion */}
                {!showBig && (
                    <div style={abs({ left: 300, top: 248, width: 200, display: "flex", justifyContent: "center" })}>
                        <Digits value={endedAt !== null ? 0 : (pot ?? 0)} src={`${WH}/yellow_numeral.png`} w={15} h={22} />
                    </div>
                )}
                {showBig && (
                    <>
                        <img alt="" src={`${WH}/bonus.png`} style={abs({ left: 230, top: 180 })} />
                        <div style={abs({ left: 100, top: 270, width: 600, display: "flex", justifyContent: "center" })}>
                            <Digits value={left} src={`${WH}/green_numeral.png`} w={76} h={87} gap={-6} />
                        </div>
                    </>
                )}
            </div>
            {celebrating && sinceStop < BIG_AFTER_MS + 1200 && (
                <>
                    <img alt="" src={`${REF}/fountain_money/fountain_money_${pad2(Math.floor(sinceStop / 50) % 34)}.png`} style={abs({ left: 0, top: 0 })} />
                    <img alt="" src={`${REF}/fountain_money/fountain_money_${pad2(Math.floor(sinceStop / 50) % 34)}.png`} style={abs({ left: 495, top: 0, transform: "scaleX(-1)" })} />
                </>
            )}
            {spinning && stopping === null && (
                <div className="jgb-countdown" style={abs({ left: 486, top: 519 })}>
                    <Digits value={countdown} src={`${process.env.PUBLIC_URL}/tv-milionario/footer-buttons/fnt_white.png`} w={13} h={15} />
                </div>
            )}
            <Button name="bt_init" x={678} onClick={press} lit={spinning && stopping === null}
                disabled={!(spinning && stopping === null) && !(left !== null && endedAt === null && sinceStop >= BIG_AFTER_MS + COUNT_AFTER_MS)} />
        </div>
    );
};

export default LuckyWheelBonus;
